import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, LinearProgress, Button, Link, Typography, FormControl, FormControlLabel, Checkbox } from "@material-ui/core";
import { Club } from "api/clubs";
import { patchEndDemoMode, patchSignContracts } from "api/owner/settings";
import { useSnackbar } from "notistack";
import { patchSettings } from "api/user";
import config from "config";
import i18n from "../i18n";

// prop types
interface EndDemoDialogProps {
    club?: Club,
    show: boolean,
    onClose: () => void,
    onFetchUserProfile: () => void,
    onSetClub: (club: Club) => void,
}

// end demo mode dialog
const EndDemoDialog = ({ club, show, onClose, onFetchUserProfile, onSetClub }: EndDemoDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [acceptContracts, setAcceptContracts] = useState(club?.contractsHaveBeenSigned || false);
    const [isLoading, setLoading] = useState(false);

    // execute action
    const execute = async () => {
        setLoading(true);
        try {
            let updatedClub = await patchEndDemoMode({ clubId: club!.id });
            if (!club?.contractsHaveBeenSigned) updatedClub = await patchSignContracts({ clubId: club!.id });
            await patchSettings({ onboardingStep: 5 });
            onSetClub(updatedClub);
            enqueueSnackbar(i18n.endDemoSuccess, { variant: "success" });
            onClose();
            onFetchUserProfile();
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // render
    return (
        <Dialog open={show} onClose={onClose}>
            <DialogTitle>{i18n.migrateNow}</DialogTitle>
            <DialogContent dividers>
                {club?.demoClubCanGoLive ? (
                    <DialogContentText>
                        {i18n.endDemoDesc}
                        {` ${i18n.endDemoCosts} (`}
                        <Link href="https://help.playsports.world/article/140-wie-verdient-ihr-euer-geld-da-wir-ja-keine-nutzungsgebuhr-zahlen" target="_blank">Infos</Link>
                        ).
                        {!club?.contractsHaveBeenSigned && (
                            <>
                                <Typography style={{ marginTop: "16px" }}>
                                    {i18n.contractsRequired}
                                    <Link
                                        href={config.documents.contractSharedResponsibility}
                                        style={{ display: "block", margin: "8px" }}
                                        target="_blank"
                                    >
                                        Dokument: Gemeinsame Verantwortung
                                    </Link>
                                </Typography>
                                <FormControl fullWidth>
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                checked={acceptContracts}
                                                color="primary"
                                                onChange={(e) => setAcceptContracts(e.target.checked)}
                                            />
                                        )}
                                        label={i18n.acceptContracts}
                                    />
                                </FormControl>
                            </>
                        )}
                    </DialogContentText>
                ) : (
                    <DialogContentText>
                        {i18n.endDemoDisabled}
                    </DialogContentText>
                )}
            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button color="primary" disabled={isLoading || !acceptContracts || !club?.demoClubCanGoLive} onClick={execute}>
                    {i18n.endDemo}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EndDemoDialog;
